import Row from 'react-bootstrap/Row';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import "./bookDashboardList.css";
import { clearData } from '../../../utils/localStorage';
import { bookLibraryPurchasedListService } from "../../../modules/services";
import SideBar from "../../commanComponents/sidebar/sidebar";
import { sidebarNavItems, ROUTES, EMPTY_IMAGE } from "../../../constant";
import TopNavigation from "../../commanComponents/topNavigation/topNavigation";
import PurchasedBookCard from './purchasedBookCard';
import SearchBar from '../../commanComponents/searchBar/searchBar';

const skeletonCards = Array.from({ length: 8 }, (_, index) => index + 1);

const BookDashboardList = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [data, setData] = useState([]);
    const latestRequestRef = useRef(0);
    
    const getCourseListData = useCallback(async (searchVal = "") => {
        const requestId = latestRequestRef.current + 1;
        latestRequestRef.current = requestId;

        setIsLoading(true);

        const responseData = await bookLibraryPurchasedListService({
            pagination: false,
            search: encodeURIComponent(searchVal.trim()),
        });

        if (requestId !== latestRequestRef.current) return;

        if (responseData !== "error" && (responseData.status_code === 200 || responseData.status_code === 201)) {
            const responseList = Array.isArray(responseData.data)
                ? responseData.data
                : responseData.data?.results || [];
            setData(responseList);
            setHasLoadedOnce(true);
            setIsLoading(false);
        }
        else if(responseData !== "error" && (responseData.status_code !== 200 || responseData.status_code !== 201)) {
            setData([]);
            setHasLoadedOnce(true);
            setIsLoading(false);
        }
        else {
            clearData();
            navigate(ROUTES.LOGIN);
        }
    }, [navigate]);

    useEffect(() => {
        getCourseListData("");

    }, [getCourseListData]);

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    const handleSearch = useCallback((value) => {
        getCourseListData(value);
    }, [getCourseListData]);

    const isInitialLoading = isLoading && !hasLoadedOnce;

    const renderSkeletonCards = () => (
        <Row xs={1} sm={2} md={3} lg={4} xl={4} className="g-4 p-2 my-books-skeleton-grid" aria-busy="true" aria-label="Loading purchased books">
            {skeletonCards.map((item) => (
                <div className="col" key={item}>
                    <article className="my-books-skeleton-card">
                        <div className="my-books-skeleton my-books-skeleton-cover">
                            <div className="my-books-skeleton-badges">
                                <span className="my-books-skeleton my-books-skeleton-badge"></span>
                                <span className="my-books-skeleton my-books-skeleton-badge short"></span>
                            </div>
                        </div>
                        <div className="my-books-skeleton-info">
                            <div className="my-books-skeleton my-books-skeleton-title"></div>
                            <div className="my-books-skeleton my-books-skeleton-title short"></div>
                            <div className="my-books-skeleton-lines">
                                <div className="my-books-skeleton my-books-skeleton-line"></div>
                                <div className="my-books-skeleton my-books-skeleton-line medium"></div>
                            </div>
                            <div className="my-books-skeleton-meta">
                                <div className="my-books-skeleton my-books-skeleton-chip"></div>
                                <div className="my-books-skeleton my-books-skeleton-rating"></div>
                            </div>
                            <div className="my-books-skeleton-progress">
                                <div className="my-books-skeleton my-books-skeleton-label"></div>
                                <div className="my-books-skeleton my-books-skeleton-meter"></div>
                            </div>
                        </div>
                    </article>
                </div>
            ))}
        </Row>
    );
    
    return (
        <div className='academic-courses-container' style={{ display: 'flex' }}>
            <SideBar activeItem={sidebarNavItems.bookDashboard} />
            <div className='courses-side-container'>
                <TopNavigation activeItem={sidebarNavItems.bookDashboard} />
                
                <div className={`row academic-courses-filter-container ${isInitialLoading ? "my-books-filter-skeleton" : ""}`}>
                    <div className='col academic-courses-filter-col'>
                        {isInitialLoading ? (
                            <>
                                <div className="my-books-skeleton my-books-skeleton-page-title"></div>
                                <div className="my-books-skeleton my-books-skeleton-page-subtitle"></div>
                            </>
                        ) : (
                            <div className={`my-books-list-heading ${isSearchFocused ? 'search-active-hide' : ''}`}>
                                <h2>My Books</h2>
                                <p>{data.length} {data.length === 1 ? "book" : "books"} found in your library</p>
                            </div>
                        )}
                    </div>
                    <div className='col academic-courses-filter-col my-books-search-col'>
                        {isInitialLoading ? (
                            <div className="my-books-skeleton-filter-actions">
                                <div className="my-books-skeleton my-books-skeleton-filter-pill"></div>
                                <div className="my-books-skeleton my-books-skeleton-filter-pill short"></div>
                            </div>
                        ) : (
                            <SearchBar
                                placeholder="Search your books..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onSearch={handleSearch}
                                onFocusChange={setIsSearchFocused}
                                className="my-books-search-bar"
                            />
                        )}
                    </div>
                </div>
                
                <div className='academic-courses-main-container' style={{padding: "5px"}}>
                    {
                        isLoading ?
                            renderSkeletonCards()
                            :
                            <Row xs={1} sm={2} md={3} lg={4} xl={4} className="g-4 p-2 my-books-card-grid">
                                {
                                    data.length === 0 ?
                                        <div className='academic-course-empty'>
                                            <img
                                                src={EMPTY_IMAGE}
                                                className='academic-course-empty-img'
                                                alt="coming soon"
                                            />
                                            <p>{searchTerm ? "No books matched your search." : "No purchased books found."}</p>
                                        </div>  
                                    :
                                        data.map((dataRow, index) => {                            
                                            return (
                                                <div className="col" key={dataRow.id}>
                                                    <PurchasedBookCard {...dataRow} />
                                                </div>
                                            )
                                        })
                                }
                            </Row>
                    }
                </div>
            </div>
        </div>
    );
 }


export default BookDashboardList;
